import React, { useState } from 'react'
import { Select, Space } from 'antd'

import { useAppDispatch } from 'src/store'
import { USER_TREATMENT_STATE } from 'src/enum/userTreatmentState'
import CommonModal from 'src/components/Modal/CommonModal'
import ConfirmPlanModal from 'src/components/Modal/ConfirmPlanModal'
import ButtonCustom from 'src/components/form/Button'
import uiActions from 'src/store/ui/notification'
import {
  cancelUserTreatment,
  getCancelModalTreatments,
} from 'src/useCase/userTreatment'

const { Option } = Select
const FAILED = 'failed'

const DEFAULT_EXCLUDED_STATES = [
  USER_TREATMENT_STATE.CANCEL_REQUESTED,
  USER_TREATMENT_STATE.CANCELED,
  USER_TREATMENT_STATE.DOCTOR_CANCEL_PRESCRIPTION,
  USER_TREATMENT_STATE.DOCTOR_PRESCRIPTION_OK,
  USER_TREATMENT_STATE.SUBSCRIPTION_CONTRACT,
]

type Props = {
  onCancelSuccess?: () => void
  excludedStates?: string[]
  includedStates?: string[]
}

const CancelTreatmentSection: React.FC<Props> = ({
  onCancelSuccess,
  excludedStates = DEFAULT_EXCLUDED_STATES,
  includedStates,
}) => {
  const dispatch = useAppDispatch()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [cancelModalTreatments, setCancelModalTreatments] = useState<any[]>([])
  const [isLoadingCancelModal, setIsLoadingCancelModal] = useState(false)
  const [cancelSelectedTreatmentId, setCancelSelectedTreatmentId] = useState<
    number | null
  >(null)
  const [isSubmitCancelTreatment, setIsSubmitCancelTreatment] = useState(false)
  const [isModalConfirmOpen, setIsModalConfirmOpen] = useState(false)
  const [stateBefore, setStateBefore] = useState('')

  const handleOpenModal = async () => {
    setIsLoadingCancelModal(true)
    try {
      const stateFilter: Record<string, string> = includedStates?.length
        ? { state: includedStates.join(',') }
        : { not_state: excludedStates.join(',') }
      const treatments = await getCancelModalTreatments({
        with: 'menu',
        ...stateFilter,
      })
      setCancelModalTreatments(treatments ?? [])
      setCancelSelectedTreatmentId(null)
      setIsModalOpen(true)
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        'エラーが発生しました。再度お試しください。'
      dispatch(uiActions.onNotification({ type: 'error', message }))
    } finally {
      setIsLoadingCancelModal(false)
    }
  }

  const handleConfirmCancel = async () => {
    if (!cancelSelectedTreatmentId) return
    try {
      setIsSubmitCancelTreatment(true)
      const res = await cancelUserTreatment(cancelSelectedTreatmentId)
      setStateBefore(res?.data?.data?.state_before || 'success')
      setIsModalConfirmOpen(true)
    } catch {
      setStateBefore(FAILED)
      setIsModalConfirmOpen(true)
    } finally {
      setIsSubmitCancelTreatment(false)
      setIsModalOpen(false)
    }
  }

  const handleCloseConfirmModal = () => {
    setIsModalConfirmOpen(false)
    // [SCOPE] Refresh list only after user dismisses success modal.
    // Pre-existing: onCancelSuccess was called before opening the modal, causing
    // userTreatments to clear (entities=[]) and unmount this component, losing
    // isModalConfirmOpen=true before the modal could render.
    // New behavior: list refreshes after modal closes — safe, only affects this component.
    if (stateBefore && stateBefore !== FAILED) {
      onCancelSuccess?.()
    }
  }

  const handleCloseSelectModal = () => {
    setIsModalOpen(false)
  }

  const handleSelectTreatment = (value: any) => {
    if (value) {
      setCancelSelectedTreatmentId(value)
    }
  }

  return (
    <>
      <div className="d-flex justify-center mt-80">
        <div className="d-flex justify-center mx-auto">
          <button
            onClick={handleOpenModal}
            disabled={isLoadingCancelModal}
            type="button"
            className="btn btn-line"
          >
            キャンセルのお申し込みはこちら
          </button>
        </div>
      </div>

      <ConfirmPlanModal isOpen={isModalOpen} onCancel={handleCloseSelectModal}>
        <div className="p-15 text-center mt-20">
          <h3 className="text-title">
            キャンセルをお申し込みされる診療科目を選択してください
          </h3>

          <Space wrap>
            <Select
              style={{
                width: 220,
                height: 60,
                textAlign: 'left',
                marginBottom: 10,
              }}
              onChange={handleSelectTreatment}
            >
              {cancelModalTreatments.map((userTreatment, index) => (
                <Option value={userTreatment?.id} key={index}>
                  {userTreatment?.menu?.name}
                </Option>
              ))}
            </Select>
          </Space>

          <p>申し込みボタンを押すと、キャンセルのお申し込みが送信されます。</p>
          <p>キャンセルをお申し込みされる場合はボタンを押してください。</p>
          <ButtonCustom
            className="btn-default btn-confirm mt-3"
            loading={isSubmitCancelTreatment}
            disabled={!cancelSelectedTreatmentId}
            onClick={handleConfirmCancel}
            htmlType="submit"
            name="キャンセルを申し込む"
            showIconRight
          />
        </div>
      </ConfirmPlanModal>

      <CommonModal
        isOpen={isModalConfirmOpen}
        onClose={handleCloseConfirmModal}
      >
        {stateBefore && stateBefore !== FAILED ? (
          <div className="p-15 text-center mt-20">
            <p>
              この度はキャンセルのお申し込みをいただき、誠にありがとうございます。
            </p>
            <p>
              現在、順次キャンセル手続きを進めております。
              <br />
              手続きの完了までに1〜3営業日ほどお時間をいただく場合がございますので、あらかじめご了承ください。
            </p>
            <p>
              手続きが完了いたしましたら、改めてメールにてご連絡いたします。
              <br />
              今しばらくお待ちいただけますよう、何卒よろしくお願い申し上げます。
            </p>
          </div>
        ) : (
          <div className="p-15 text-center mt-20">
            <h3 className="text-title">キャンセル処理ができませんでした。</h3>
            <p>以下サポート窓口までお問い合わせください。</p>
            <p>
              オンライン処方サポート：
              <a
                href="mailto:bioportplus@sheepmedical.com"
                className="color-text-blue"
              >
                bioportplus@sheepmedical.com
              </a>
            </p>
          </div>
        )}
      </CommonModal>
    </>
  )
}

export default CancelTreatmentSection
